import React from 'react'
import { gql } from '@apollo/client'
import Head from 'next/head'
import { useRouter } from 'next/router'
import CategoryFAQ from 'components/category/CategoryFaq'
import { ArticleJsonLd } from 'components/shared/schema/article'
import ApplicationLayout from 'layouts/application'
import { PAGE_SIZE_FOR_PAGINATION } from 'shared/constants'
import { pageUri } from 'shared/util'
import { initializeApollo, addApolloState } from 'util/apolloClient'

const title = ({ category, page }) => {
  return `Frequently asked questions about ${category.attributes.name} - Page ${
    page || 1
  }`
}

const description = ({ category }) => {
  return `All the finance related frequently asked questions(FAQs) and answers about ${category.attributes.name}`
}

const GET_BROKER_WITH_NAV = gql`
  query categories($slug: String, $start: Int) {
    all_brokers: brokers {
      data {
        attributes {
          ...AllBrokers
          broker_type
        }
      }
    }
    all_pages: pages {
      data{
        attributes {
          ...AllPages
        }
      }
    }
    categories(filters: {slug : {eq : $slug}}) {
      data {
        attributes {
          name
          categoriesArticlesCount

          relatedCategories{
            name
            slug
          }
          softwares {
            data {
              attributes {
                name
                slug
              }
            }
          }

          articles(
            filters: {article_type: {eq: "faq"},status: {eq: "published"}},
            sort: ["updatedAt:desc"],
            pagination: {limit: ${PAGE_SIZE_FOR_PAGINATION}, start: $start}
          ) {
            data {
              attributes {
                slug
                title
                short_description
                description
                broker {
                  data {
                    attributes {
                      name
                      slug
                    }
                  }
                }
                software{
                  data {
                    attributes {
                      name
                      slug
                    }
                  }
                }
                categories {
                  data{
                    attributes {
                      name
                      slug
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    nav_calculators: calculators(sort: ["createdAt:asc"], pagination : { limit : 5}) {
      data {
        attributes {
          ...NavCalculators
        }
      }
    }
  }
  ${ApplicationLayout.fragments.all_brokers}
  ${ApplicationLayout.fragments.nav_calculators}
  ${ApplicationLayout.fragments.all_pages}
`

export default function Category(props) {
  const router = useRouter()
  const { page } = router.query
  const {
    asPath,
    query: { slug },
  } = router
  const { data } = props

  const {
    categories,
    all_brokers: allBrokers,
    nav_calculators: navCalculators,
    all_pages: allPages,
  } = data
  const category = categories.data[0]
  const pageTitle = title({ category, page })
  const pageDescription = description({ category })
  const urlWithoutPage = pageUri(`/faqs/${slug}`)

  return (
    <ApplicationLayout
      allBrokers={allBrokers.data}
      navCalculators={navCalculators.data}
      allPages={allPages.data}
    >
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {page === '1' && <link rel="canonical" href={urlWithoutPage} />}
      </Head>
      <ArticleJsonLd
        url={pageUri(asPath)}
        title={pageTitle}
        description={pageDescription}
      />
      <CategoryFAQ category={category} />
    </ApplicationLayout>
  )
}

export async function getServerSideProps(context) {
  const apolloClient = initializeApollo()
  const {
    query: { slug, page = 0 },
  } = context
  const start = page > 1 ? (page - 1) * PAGE_SIZE_FOR_PAGINATION : 0

  const queryResults = await apolloClient.query({
    query: GET_BROKER_WITH_NAV,
    variables: {
      slug,
      start,
    },
  })

  const { data } = queryResults

  if (data.categories.data.length === 0) {
    return {
      notFound: true,
    }
  }

  return addApolloState(apolloClient, {
    props: queryResults,
  })
}
